import React from 'react';
import { Theme } from '@mui/material';
import { TFunction } from 'i18next';
import { ComponentConfig } from '@/components/componentRegistry';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { configIcons } from '@/assets/icons';
import type { MappedLoanOfficer } from '@/services/mappers/loanOfficer.mapper';

interface LandingLoanOfficerConfigParams {
  theme: Theme;
  t: TFunction;
  onContinue: () => void;
  onBack: () => void;
  loanOfficer: MappedLoanOfficer | null;
  isLoading?: boolean;
}

export const getLandingLoanOfficerConfig = ({
  theme,
  t,
  onContinue,
  onBack,
  loanOfficer,
  isLoading = false,
}: LandingLoanOfficerConfigParams): ComponentConfig => {
  const officerName =
    loanOfficer?.name || t('landingLoanOfficer.noOfficerSelected');
  const officerNMLS =
    loanOfficer?.nmls || t('landingLoanOfficer.nmlsNotAvailable');

  return {
    component: 'Box',
    props: {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      },
    },
    children: [
      // Content Box - Flexible space with Card
      {
        component: 'Box',
        props: {
          sx: {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: { xs: theme.unit(4), md: theme.spacing(4) },
          },
        },
        children: [
          {
            component: 'Card',
            props: {
              sx: {
                width: '100%',
                maxWidth: { xs: '100%', sm: '90%', md: '877px' },
                borderRadius: theme.unit(2.5),
                border: `1px solid ${theme.palette.M3?.sys?.light?.['card-border']}`,
                padding: {
                  xs: theme.unit(6),
                  sm: theme.unit(8),
                  md: theme.unit(12),
                },
                backgroundColor: theme.palette.M3?.white,
                boxShadow:
                  '0px 2px 6px 2px #00000014, 0px 1px 2px 0px #00000014',
              },
            },
            children: [
              {
                component: 'Box',
                children: [
                  {
                    component: 'ColumnLayout',
                    props: {
                      gap: { xs: theme.unit(6), md: theme.unit(8) },
                      fullWidth: true,
                    },
                    children: [
                      {
                        component: 'ColumnLayout',
                        props: {
                          gap: theme.unit(5),
                          fullWidth: true,
                        },
                        children: [
                          {
                            component: 'ColumnLayout',
                            props: {
                              gap: theme.unit(4),
                              fullWidth: true,
                            },
                            children: [
                              {
                                component: 'Typography',
                                props: {
                                  sx: {
                                    ...theme.typography.M3.headline.large,
                                    fontWeight: 700,
                                    fontSize: {
                                      xs: theme.unit(6),
                                      md: theme.unit(8),
                                    },
                                    color:
                                      theme.palette.M3?.sys?.light?.[
                                        'on-primary-fixed'
                                      ],
                                    textAlign: 'center',
                                  },
                                  children: t('landingLoanOfficer.headline'),
                                },
                              },
                              {
                                component: 'Typography',
                                props: {
                                  sx: {
                                    ...theme.typography.M3.title.medium,
                                    fontWeight: 700,
                                    fontSize: {
                                      xs: theme.unit(4),
                                      md: theme.unit(5),
                                    },
                                    color:
                                      theme.palette.M3?.sys?.light?.[
                                        'on-surface-variant'
                                      ],
                                    textAlign: 'center',
                                  },
                                  children: t('landingLoanOfficer.subtitle'),
                                },
                              },
                            ],
                          },
                          {
                            component: 'Box',
                            props: {
                              sx: {
                                padding: theme.unit(4),
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                              },
                            },
                            children: [
                              {
                                component: 'ColumnLayout',
                                props: {
                                  gap: theme.unit(4),
                                  fullWidth: true,
                                },
                                children: [
                                  {
                                    component: 'Box',
                                    props: {
                                      sx: {
                                        width: { xs: '80px', md: '110px' },
                                        height: { xs: '80px', md: '110px' },
                                        borderRadius: '50%',
                                        border: `1px solid ${theme.palette.M3?.sys?.light?.outline}`,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        alignSelf: 'center',
                                      },
                                    },
                                    children: [
                                      {
                                        component: 'Box',
                                        props: {
                                          component: 'img',
                                          src:
                                            loanOfficer?.photo ||
                                            configIcons.revolutionLogo,
                                          alt: 'Profile',
                                          sx: {
                                            width: !loanOfficer?.photo
                                              ? '80%'
                                              : '100%',
                                            height: !loanOfficer?.photo
                                              ? '80%'
                                              : '100%',
                                            objectFit: !loanOfficer?.photo
                                              ? 'contain'
                                              : 'cover',
                                            borderRadius: '50%',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    component: 'ColumnLayout',
                                    props: {
                                      gap: theme.unit(2),
                                      fullWidth: true,
                                    },
                                    children: [
                                      {
                                        component: 'Typography',
                                        props: {
                                          sx: {
                                            ...theme.typography.M3.headline
                                              .small,
                                            fontWeight: 700,
                                            fontSize: {
                                              xs: theme.unit(5),
                                              md: theme.unit(6),
                                            },
                                            color:
                                              theme.palette.M3?.sys?.light?.[
                                                'on-primary-fixed'
                                              ],
                                            textAlign: 'center',
                                          },
                                          children: officerName,
                                        },
                                      },
                                      {
                                        component: 'Box',
                                        props: {
                                          sx: {
                                            display: 'flex',
                                            justifyContent: 'center',
                                          },
                                        },
                                        children: [
                                          {
                                            component: 'ColumnLayout',
                                            props: {
                                              gap: theme.unit(2),
                                              fullWidth: true,
                                            },
                                            children: [
                                              {
                                                component: 'Typography',
                                                props: {
                                                  sx: {
                                                    ...theme.typography.M3.label
                                                      .large,
                                                    fontWeight: 700,
                                                    color:
                                                      theme.palette.M3?.sys
                                                        ?.light?.[
                                                        'on-surface-variant'
                                                      ],
                                                    textAlign: 'center',
                                                  },
                                                  children: t(
                                                    'PageLoanOfficer.loanOfficerLabel'
                                                  ),
                                                },
                                              },
                                              {
                                                component: 'Typography',
                                                props: {
                                                  sx: {
                                                    ...theme.typography.M3.label
                                                      .medium,
                                                    fontWeight: 500,
                                                    color:
                                                      theme.palette.M3?.sys
                                                        ?.light?.[
                                                        'on-surface-variant'
                                                      ],
                                                    textAlign: 'center',
                                                  },
                                                  children: `${t('landingLoanOfficer.nmls')} #${officerNMLS}`,
                                                },
                                              },
                                            ],
                                          },
                                        ],
                                      },
                                    ],
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                      {
                        component: 'RowLayout',
                        props: {
                          justifyContent: 'space-between',
                          fullWidth: true,
                          sx: {
                            minWidth: { xs: 'auto', md: '780px' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: theme.unit(3), sm: theme.unit(4) },
                          },
                        },
                        children: [
                          {
                            component: 'Button',
                            props: {
                              variant: 'outlined',
                              size: 'l',
                              onClick: onBack,
                              startIcon: React.createElement(ArrowBackIcon),
                              sx: {
                                ...theme.typography.M3.label.large,
                                order: { xs: 2, sm: 1 },
                                width: { xs: '100%', sm: 'auto' },
                                height: { xs: theme.unit(12), sm: 'auto' },
                              },
                              children: t('actions.back'),
                            },
                          },
                          {
                            component: 'Button',
                            props: {
                              variant: 'filled',
                              onClick: onContinue,
                              sx: {
                                paddingY: theme.unit(2.5),
                                paddingX: theme.unit(6),
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                ...theme.typography.M3.label.large,
                                order: { xs: 1, sm: 2 },
                                width: { xs: '100%', sm: 'auto' },
                                height: { xs: theme.unit(12), sm: 'auto' },
                              },
                              children: t(
                                'landingLoanOfficer.startApplication'
                              ),
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
};
